import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, Send, FileText, LayoutGrid, CarFront } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PageHero } from "../components/PageHero";
import { EmployerOverview, DestinationTabs } from "../components/landing/Overview";
import { Industries } from "../components/landing/Showcase";
import { Reveal, Overline } from "../components/Reveal";
import { useLang } from "../context/LanguageContext";
import { CATEGORY_KEYS } from "../i18n/translations";
import { api } from "../lib/api";

const emptyReq = {
  category: "healthcare",
  other_category: "",
  workers: 10,
  salary: "",
  profession_on_visa: "",
  experience_years: "3",
  gulf_experience: "yes",
  consulate: "mumbai",
  driver_license: "",
  driver_for: "",
  duty_hours: "8",
  shift: "1",
};

export default function EmployerPage() {
  const { t } = useLang();
  const { hash } = useLocation();
  const [form, setForm] = useState({ company_name: "", entity_type: "muassasa", contact_person: "", phone: "", email: "", notes: "" });
  const [req, setReq] = useState({ ...emptyReq });
  const [added, setAdded] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (hash === "#inquiry") {
      setTimeout(() => document.getElementById("inquiry")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [hash]);

  const isDriver = req.category === "driver";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setR = (k, v) => setReq((r) => ({ ...r, [k]: v }));

  const addCategory = () => {
    const label = req.category === "other" ? req.other_category || t("categories.other") : t(`categories.${req.category}`);
    setAdded((a) => [...a, { ...req, label }]);
    setReq({ ...emptyReq });
    toast.success(label);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (added.length === 0) {
      toast.error(t("form.required"));
      return;
    }
    setSending(true);
    try {
      await api.post("/inquiries", { ...form, categories: added });
      toast.success(t("form.success"));
      setForm({ company_name: "", entity_type: "muassasa", contact_person: "", phone: "", email: "", notes: "" });
      setAdded([]);
    } catch {
      toast.error(t("form.error"));
    } finally {
      setSending(false);
    }
  };

  const entityTypes = ["muassasa", "shirka", "individual"];

  return (
    <div className="bg-[#FAF7F2] min-h-screen" data-testid="employer-page">
      <Header />

      <PageHero overline={t("employer.overline")} title={t("employer.title")} testid="employer-title" img="/assets/bg-employers.jpg" />

      <EmployerOverview />

      <section className="py-16 px-6 sm:px-12" data-testid="employer-terms">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-11 h-11 rounded-xl bg-[#E8842C] flex items-center justify-center"><FileText className="w-5 h-5 text-white" /></div>
              <h2 className="font-head font-black text-2xl sm:text-3xl text-[#1B2A4E]">{t("employer.tc_title")}</h2>
            </div>
          </Reveal>
          <div className="space-y-4">
            {t("employer.tc").map((item, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="flex gap-4 items-start card-light p-5" data-testid={`employer-term-${i}`}>
                  <span className="font-head font-black text-[#E8842C] text-lg shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-sm text-[#1B2A4E]/75 leading-relaxed">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <div className="py-6">
        <div className="max-w-6xl mx-auto px-6 sm:px-12">
          <Reveal>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#1B2A4E] flex items-center justify-center"><LayoutGrid className="w-5 h-5 text-white" /></div>
              <h2 className="font-head font-black text-2xl sm:text-3xl text-[#1B2A4E]">{t("employer.cats_title")}</h2>
            </div>
          </Reveal>
        </div>
      </div>
      <Industries />

      <DestinationTabs ns="empov" testid="employer-process" />

      <section id="inquiry" className="py-24 px-6 sm:px-12" data-testid="inquiry-section">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <Overline>{t("employer.form_title")}</Overline>
            <h2 className="font-head font-black text-2xl sm:text-4xl text-[#1B2A4E] mb-3">{t("employer.form_title")}</h2>
            <p className="text-[#1B2A4E]/60 mb-12">{t("employer.form_sub")}</p>
          </Reveal>

          <form onSubmit={submit} className="space-y-8">
            <div className="rounded-3xl border border-[#1B2A4E]/10 bg-white p-7 sm:p-9 space-y-6 shadow-sm">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="label">{t("form.company_name")} *</label>
                  <input data-testid="input-company-name" required className="field" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} />
                </div>
                <div>
                  <label className="label">{t("form.entity_type")} *</label>
                  <div className="flex gap-2" data-testid="entity-type-group">
                    {entityTypes.map((et) => (
                      <button
                        type="button"
                        key={et}
                        data-testid={`entity-${et}`}
                        onClick={() => set("entity_type", et)}
                        className={`flex-1 py-2.5 rounded-xl text-xs font-bold border transition-all ${
                          form.entity_type === et ? "bg-[#1B2A4E] border-[#1B2A4E] text-white" : "border-[#1B2A4E]/20 text-[#1B2A4E]/60 hover:border-[#E8842C]"
                        }`}
                      >
                        {t(`form.${et}`)}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="label">{t("form.contact_person")}</label>
                  <input data-testid="input-contact-person" className="field" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} />
                </div>
                <div>
                  <label className="label">{t("form.phone")} *</label>
                  <input data-testid="input-phone" required className="field" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className="label">{t("form.email")}</label>
                  <input data-testid="input-email" type="email" className="field" value={form.email} onChange={(e) => set("email", e.target.value)} />
                </div>
              </div>
            </div>

            <div className="rounded-3xl border-2 border-[#E8842C]/40 bg-white p-7 sm:p-9 space-y-5 shadow-sm" data-testid="category-builder">
              <h3 className="font-head font-bold text-lg text-[#1B2A4E] flex items-center gap-2">
                <CarFront className="w-5 h-5 text-[#E8842C]" /> {t("form.category")}
              </h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="label">{t("form.category")}</label>
                  <select data-testid="select-category" className="field" value={req.category} onChange={(e) => setR("category", e.target.value)}>
                    {CATEGORY_KEYS.map((k) => (
                      <option key={k} value={k}>{t(`categories.${k}`)}</option>
                    ))}
                  </select>
                </div>
                {req.category === "other" && (
                  <div>
                    <label className="label">{t("form.category_other")}</label>
                    <input data-testid="input-other-category" className="field" value={req.other_category} onChange={(e) => setR("other_category", e.target.value)} />
                  </div>
                )}
                <div>
                  <label className="label">{t("form.workers")}</label>
                  <input data-testid="input-workers" type="number" min="1" className="field" value={req.workers} onChange={(e) => setR("workers", Number(e.target.value))} />
                </div>
                <div>
                  <label className="label">{t("form.salary")}</label>
                  <input data-testid="input-salary" type="number" min="0" className="field" value={req.salary} onChange={(e) => setR("salary", e.target.value)} />
                </div>
                <div>
                  <label className="label">{t("form.profession_visa")}</label>
                  <input data-testid="input-profession-visa" className="field" value={req.profession_on_visa} onChange={(e) => setR("profession_on_visa", e.target.value)} />
                </div>
                <div>
                  <label className="label">{t("form.experience")}</label>
                  <select data-testid="select-experience" className="field" value={req.experience_years} onChange={(e) => setR("experience_years", e.target.value)}>
                    {Array.from({ length: 15 }, (_, i) => i + 1).map((y) => (
                      <option key={y} value={y}>{y} {t("form.years")}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">{t("form.gulf_exp")}</label>
                  <div className="flex gap-2" data-testid="gulf-exp-group">
                    {["yes", "no"].map((v) => (
                      <button
                        type="button"
                        key={v}
                        data-testid={`gulf-exp-${v}`}
                        onClick={() => setR("gulf_experience", v)}
                        className={`flex-1 py-2.5 rounded-xl text-xs font-bold border transition-all ${
                          req.gulf_experience === v ? "bg-[#1B2A4E] border-[#1B2A4E] text-white" : "border-[#1B2A4E]/20 text-[#1B2A4E]/60 hover:border-[#E8842C]"
                        }`}
                      >
                        {t(`form.${v}`)}
                      </button>
                    ))}
                  </div>
                  {req.gulf_experience === "no" && <p className="text-[11px] text-[#E8842C] font-bold mt-2">{t("form.gulf_hint")}</p>}
                </div>
                <div>
                  <label className="label">{t("form.consulate")}</label>
                  <select data-testid="select-consulate" className="field" value={req.consulate} onChange={(e) => setR("consulate", e.target.value)}>
                    <option value="mumbai">{t("form.mumbai")}</option>
                    <option value="delhi">{t("form.delhi")}</option>
                  </select>
                </div>

                {isDriver && (
                  <>
                    <div>
                      <label className="label">{t("form.driver_license")}</label>
                      <select data-testid="select-driver-license" className="field" value={req.driver_license} onChange={(e) => setR("driver_license", e.target.value)}>
                        <option value="">—</option>
                        <option value="with_saudi">{t("form.with_saudi")}</option>
                        <option value="without">{t("form.without_license")}</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">{t("form.driver_for")}</label>
                      <select data-testid="select-driver-for" className="field" value={req.driver_for} onChange={(e) => setR("driver_for", e.target.value)}>
                        <option value="">—</option>
                        <option value="house">{t("form.house")}</option>
                        <option value="company">{t("form.company")}</option>
                      </select>
                    </div>
                  </>
                )}

                <div>
                  <label className="label">{t("form.duty")}</label>
                  <select data-testid="select-duty" className="field" value={req.duty_hours} onChange={(e) => setR("duty_hours", e.target.value)}>
                    <option value="8">8 {t("form.hours")}</option>
                    <option value="10">10 {t("form.hours")}</option>
                  </select>
                </div>
                <div>
                  <label className="label">{t("form.shift")}</label>
                  <select data-testid="select-shift" className="field" value={req.shift} onChange={(e) => setR("shift", e.target.value)}>
                    {["1", "2", "3"].map((s) => (
                      <option key={s} value={s}>{t("form.shift")} {s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button type="button" data-testid="add-category-btn" onClick={addCategory} className="btn-ghost !py-2.5 !px-5 text-sm">
                <Plus className="w-4 h-4" /> {t("form.add_category")}
              </button>
            </div>

            {added.length > 0 && (
              <div className="rounded-3xl border border-[#1B2A4E]/10 bg-[#1B2A4E]/[0.03] p-7" data-testid="added-requirements">
                <h3 className="font-head font-bold text-lg text-[#1B2A4E] mb-5">{t("form.added")} ({added.length})</h3>
                <div className="space-y-3">
                  {added.map((a, i) => (
                    <div key={i} className="flex items-center justify-between gap-4 rounded-xl border border-[#1B2A4E]/10 bg-white px-5 py-4" data-testid={`added-req-${i}`}>
                      <div className="text-sm">
                        <span className="font-bold text-[#E8842C]">{a.label}</span>
                        <span className="text-[#1B2A4E]/60 ms-3">
                          {a.workers} × {a.salary ? `${a.salary} SAR` : "—"} • {a.experience_years} {t("form.years")} • {t(`form.${a.gulf_experience}`)} • {t(`form.${a.consulate}`)}
                          {a.category === "driver" && a.driver_license ? ` • ${t(`form.${a.driver_license === "with_saudi" ? "with_saudi" : "without_license"}`)}` : ""}
                          {a.category === "driver" && a.driver_for ? ` • ${t(`form.${a.driver_for}`)}` : ""}
                          {` • ${a.duty_hours}h • ${t("form.shift")} ${a.shift}`}
                        </span>
                      </div>
                      <button type="button" data-testid={`remove-req-${i}`} onClick={() => setAdded((arr) => arr.filter((_, j) => j !== i))} className="text-[#1B2A4E]/40 hover:text-red-500 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="label">{t("form.notes")}</label>
              <textarea data-testid="input-notes" rows={3} className="field" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
            </div>

            <button type="submit" data-testid="submit-inquiry-btn" disabled={sending} className="btn-primary w-full justify-center !py-4 text-base disabled:opacity-50">
              {t("form.submit")} <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
